import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faBarsFilter as faBarsFilterSharpLight } from '@fortawesome/sharp-light-svg-icons';
import { faStar as faStarSolid } from '@fortawesome/pro-solid-svg-icons';
import {
  faAnglesDown as faAnglesDownLight,
  faAnglesUp as faAnglesUpLight,
  faArrowPointer as faArrowPointerLight,
  faArrowUpRightFromSquare as faArrowUpRightFromSquareLight,
  faArrowRotateLeft as faArrowRotateLeftLight,
  faArrowRotateRight as faArrowRotateRightLight,
  faArrowUTurnUpLeft as faArrowUTurnUpLeftLight,
  faArrowsRotate as faArrowsRotateLight,
  faBadgeCheck as faBadgeCheckLight,
  faBriefcaseArrowRight as faBriefcaseArrowRightLight,
  faCalendarClock as faCalendarClockLight,
  faCaretDown as faCaretDownLight,
  faChartColumn as faChartColumnLight,
  faCheck as faCheckLight,
  faChevronDown as faChevronDownLight,
  faChevronLeft as faChevronLeftLight,
  faChevronRight as faChevronRightLight,
  faChevronUp as faChevronUpLight,
  faCircleCheck as faCircleCheckLight,
  faCircleExclamation as faCircleExclamationLight,
  faCircleInfo as faCircleInfoLight,
  faCirclePlay as faCirclePlayLight,
  faCirclePlus as faCirclePlusLight,
  faCircleQuestion as faCircleQuestionLight,
  faCircleXmark as faCircleXmarkLight,
  faClock as faClockLight,
  faCodeSimple as faCodeSimpleLight,
  faCopy as faCopyLight,
  faDeleteLeft as faDeleteLeftLight,
  faDesktop as faDesktopLight,
  faDisplayChartUp as faDisplayChartUpLight,
  faDownload as faDownloadLight,
  faEllipsisVertical as faEllipsisVerticalLight,
  faEye as faEyeLight,
  faFilePdf as faFilePdfLight,
  faFileCheck as faFileCheckLight,
  faFileExport as faFileExportLight,
  faFileUser as faFileUserLight,
  faFire as faFireLight,
  faFloppyDisk as faFloppyDiskLight,
  faGear as faGearLight,
  faGearComplex as faGearComplexLight,
  faGlobe as faGlobeLight,
  faHouse as faHouseLight,
  faKey as faKeyLight,
  faListUl as faListUlLight,
  faLock as faLockLight,
  faMagnifyingGlass as faMagnifyingGlassLight,
  faMagnifyingGlassPlus as faMagnifyingGlassPlusLight,
  faMinus as faMinusLight,
  faPencil as faPencilLight,
  faPlus as faPlusLight,
  faReply as faReplyLight,
  faRightFromBracket as faRightFromBracketLight,
  faShareFromSquare as faShareFromSquareLight,
  faSliders as faSlidersLight,
  faStar as faStarLight,
  faStopwatch as faStopwatchLight,
  faTable as faTableLight,
  faTrashCan as faTrashCanLight,
  faTriangleExclamation as faTriangleExclamationLight,
  faUser as faUserLight,
  faUserGroup as faUserGroupLight,
  faUserGroupCrown as faUserGroupCrownLight,
  faUserPlus as faUserPlusLight,
  faXmark as faXmarkLight,
} from '@fortawesome/pro-light-svg-icons';

export interface FontAwesomeIconMapping {
  previewIcon: IconDefinition;
  plannedPrefix?: string;
  plannedIconName?: string;
  figmaToken?: string;
}

function mapToFontAwesome(
  previewIcon: IconDefinition,
  options?: Pick<FontAwesomeIconMapping, 'plannedPrefix' | 'plannedIconName' | 'figmaToken'>,
): FontAwesomeIconMapping {
  return {
    previewIcon,
    plannedPrefix: options?.plannedPrefix,
    plannedIconName: options?.plannedIconName,
    figmaToken: options?.figmaToken,
  };
}

/**
 * Single source of truth for Material icon name -> Font Awesome icon mapping.
 *
 * This is extracted from `MaterialIconsInventoryPageComponent` to be reusable by templates
 * (via pipe) and by design-system inventory tooling.
 */
export const MATERIAL_ICON_TO_FONTAWESOME: Record<string, FontAwesomeIconMapping> = {
  add: mapToFontAwesome(faPlusLight, { figmaToken: 'plus-light' }),
  add_circle_outline: mapToFontAwesome(faCirclePlusLight, { figmaToken: 'circle-plus-light' }),
  // Some feature templates still emit these icon names from `develop` merges.
  // We map them to existing Font Awesome icons to unblock finalization
  // (removing the legacy Material→FA pipe) even if the visual match isn't perfect yet.
  arrow_forward: mapToFontAwesome(faChevronRightLight),
  arrow_drop_down: mapToFontAwesome(faCaretDownLight),
  bar_chart: mapToFontAwesome(faChartColumnLight),
  backspace: mapToFontAwesome(faDeleteLeftLight),
  badgeCheck: mapToFontAwesome(faBadgeCheckLight),
  browse_activity: mapToFontAwesome(faDisplayChartUpLight, { figmaToken: 'display-chart-up-light-full' }),
  cancel: mapToFontAwesome(faCircleXmarkLight, { figmaToken: 'circle-xmark-light' }),
  cached: mapToFontAwesome(faArrowsRotateLight),
  check: mapToFontAwesome(faCheckLight),
  check_circle: mapToFontAwesome(faCircleCheckLight),
  chevron_right: mapToFontAwesome(faChevronRightLight),
  clear: mapToFontAwesome(faXmarkLight, { figmaToken: 'xmark-regular-full' }),
  close: mapToFontAwesome(faXmarkLight, { figmaToken: 'xmark-regular-full' }),
  code: mapToFontAwesome(faCodeSimpleLight),
  content_copy: mapToFontAwesome(faCopyLight),
  copy: mapToFontAwesome(faCopyLight),
  data_table: mapToFontAwesome(faTableLight),
  delete: mapToFontAwesome(faTrashCanLight),
  demography: mapToFontAwesome(faFileUserLight, { figmaToken: 'file-user-light-full' }),
  desktop_mac: mapToFontAwesome(faDesktopLight),
  download: mapToFontAwesome(faDownloadLight),
  edit: mapToFontAwesome(faPencilLight),
  error_outline: mapToFontAwesome(faCircleExclamationLight, { figmaToken: 'circle-exclamation-light' }),
  event_repeat: mapToFontAwesome(faCalendarClockLight),
  expand_less: mapToFontAwesome(faChevronUpLight),
  expand_more: mapToFontAwesome(faChevronDownLight),
  file_export: mapToFontAwesome(faFileExportLight),
  filter_alt: mapToFontAwesome(faBarsFilterSharpLight, { figmaToken: 'bars-filter-sharp-light-full' }),
  fire: mapToFontAwesome(faFireLight),
  gear: mapToFontAwesome(faGearLight),
  group_search: mapToFontAwesome(faMagnifyingGlassLight),
  help: mapToFontAwesome(faCircleQuestionLight, { figmaToken: 'circle-question-light-full' }),
  help_outline: mapToFontAwesome(faCircleQuestionLight, { figmaToken: 'circle-question-light-full' }),
  highlight_off: mapToFontAwesome(faCircleXmarkLight, { figmaToken: 'circle-xmark-light' }),
  home: mapToFontAwesome(faHouseLight, { figmaToken: 'house-light-full' }),
  info: mapToFontAwesome(faCircleInfoLight, { figmaToken: 'circle-info-regular' }),
  info_outline: mapToFontAwesome(faCircleInfoLight, { figmaToken: 'circle-info-regular' }),
  lightbulb: mapToFontAwesome(faCircleInfoLight),
  ios_share: mapToFontAwesome(faShareFromSquareLight, { figmaToken: 'share-from-square-light-full' }),
  keyboard_arrow_down: mapToFontAwesome(faChevronDownLight, { figmaToken: 'chevron-down-regular' }),
  keyboard_arrow_left: mapToFontAwesome(faChevronLeftLight),
  keyboard_arrow_right: mapToFontAwesome(faChevronRightLight),
  keyboard_double_arrow_down: mapToFontAwesome(faAnglesDownLight),
  keyboard_double_arrow_up: mapToFontAwesome(faAnglesUpLight),
  list: mapToFontAwesome(faListUlLight),
  lock: mapToFontAwesome(faLockLight),
  logout: mapToFontAwesome(faRightFromBracketLight, { figmaToken: 'right-from-bracket-light-full' }),
  more_vert: mapToFontAwesome(faEllipsisVerticalLight),
  move_location: mapToFontAwesome(faBriefcaseArrowRightLight),
  north_east: mapToFontAwesome(faArrowUpRightFromSquareLight),
  password: mapToFontAwesome(faKeyLight),
  person_add: mapToFontAwesome(faUserPlusLight),
  person_outline: mapToFontAwesome(faUserLight),
  picture_as_pdf: mapToFontAwesome(faFilePdfLight),
  play_circle: mapToFontAwesome(faCirclePlayLight, { figmaToken: 'circle-play-light-full' }),
  play_circle_outline: mapToFontAwesome(faCirclePlayLight, { figmaToken: 'circle-play-light-full' }),
  // Note: Figma’s `eye-light` appears to have an “empty pupil” (no highlight/crescent).
  // Font Awesome v7 `eye` includes an internal highlight across available packs/weights; we intentionally use the upstream FA7 icon.
  preview: mapToFontAwesome(faEyeLight, { figmaToken: 'eye-light' }),
  public: mapToFontAwesome(faGlobeLight, { figmaToken: 'globe-light-full' }),
  refresh: mapToFontAwesome(faArrowRotateRightLight),
  remove: mapToFontAwesome(faMinusLight),
  restore: mapToFontAwesome(faArrowRotateLeftLight),
  reply: mapToFontAwesome(faReplyLight),
  save: mapToFontAwesome(faFloppyDiskLight, { figmaToken: 'floppy-disk-light-full' }),
  save_alt: mapToFontAwesome(faDownloadLight),
  schedule: mapToFontAwesome(faClockLight),
  search: mapToFontAwesome(faMagnifyingGlassLight, { figmaToken: 'magnifying-glass-light' }),
  settings: mapToFontAwesome(faGearComplexLight, { figmaToken: 'gear-complex-light' }),
  // Material `star` is visually "filled"; `star_border` is the outline version.
  star: mapToFontAwesome(faStarSolid),
  star_border: mapToFontAwesome(faStarLight),
  sync: mapToFontAwesome(faArrowsRotateLight),
  task: mapToFontAwesome(faFileCheckLight),
  timer: mapToFontAwesome(faStopwatchLight),
  tune: mapToFontAwesome(faSlidersLight),
  undo: mapToFontAwesome(faArrowUTurnUpLeftLight),
  user_group: mapToFontAwesome(faUserGroupLight),
  user_group_crown: mapToFontAwesome(faUserGroupCrownLight),
  variable_insert: mapToFontAwesome(faArrowPointerLight),
  visibility: mapToFontAwesome(faEyeLight, { figmaToken: 'eye-light' }),
  warning: mapToFontAwesome(faTriangleExclamationLight),
  zoom_in: mapToFontAwesome(faMagnifyingGlassPlusLight, { figmaToken: 'magnifying-glass-plus-light-full' }),
};

export const MAT_ICON_TO_FA_ICON_FALLBACK: IconDefinition = faCircleQuestionLight;

export function getFontAwesomeMappingForMaterialIcon(
  materialIcon: string | null | undefined,
): FontAwesomeIconMapping | null {
  const normalized = (materialIcon ?? '').trim();
  if (!normalized) {
    return null;
  }
  return MATERIAL_ICON_TO_FONTAWESOME[normalized] ?? null;
}

export function getFontAwesomeIconForMaterialIcon(
  materialIcon: string | null | undefined,
): IconDefinition | null {
  return getFontAwesomeMappingForMaterialIcon(materialIcon)?.previewIcon ?? null;
}

